import { Component, useMemo } from "react";

import Cookies from "js-cookie";
import { graphql, useLazyLoadQuery } from "react-relay";

import { templateRecommendationsSearchResultsRelayQuery } from "@/__generated__/templateRecommendationsSearchResultsRelayQuery.graphql";
import {
    isContentFetchError,
    isContentNotFoundError,
    isRelayError
} from "@/content/common/errors";
import { expectContent } from "@/content/utils/expectContent";

import { CategoryTemplateOrder } from "../../config/category-template-order";
import { useQueryLocale } from "../hooks/use-query-locale";

import {
    parseSearchQuery,
    TemplateInitialSearchResults
} from "./template-initial-search-results-relay";

export function getTemplateRecommendationsSearchResultsQueryVars(
    location: Location
) {
    let anonymousId = "";
    try {
        anonymousId = JSON.parse(
            decodeURIComponent(Cookies.get("ajs_anonymous_id") || `""`)
        );
    } catch (error) {
        console.log(`Error parsing anonymousId ${error}`);
        anonymousId = Cookies.get("ajs_anonymous_id") || "";
    }
    return {
        // NOTE: There is a hardcoded exemption in GRS to return nothing when
        // the first 9 recommendations match a default set. This appears to have
        // been a specific ask, but is nonetheless very unexpected.
        // See: https://atlassian.slack.com/archives/C053051JN9Y/p1729642717332069?thread_ts=1729620526.565589&cid=C053051JN9Y
        first: 9,
        context: {
            useCase: "wacConfluenceTemplates",
            product: "marketing",
            anonymousId,
            userId: Cookies.get("__aid_user_id") || "",
            custom: {
                paidSearchTerm: new URLSearchParams(location.search).get(
                    "keyword"
                )
            }
        }
    };
}

export const TemplateRecommendationsSearchResultsRelayQuery = graphql`
    query templateRecommendationsSearchResultsRelayQuery(
        $context: GrowthRecContext!
        $first: Int
    ) {
        growthRecommendations {
            recommendations(first: $first, context: $context) {
                ... on GrowthRecRecommendations {
                    data {
                        entityId
                    }
                }
            }
        }
    }
`;

type RecommendationsErrorBoundaryProps = {
    locale: string;
    children: React.ReactNode;
};

class TemplateRecommendationsErrorBoundary extends Component<
    RecommendationsErrorBoundaryProps,
    { error?: Error }
> {
    public constructor(props: RecommendationsErrorBoundaryProps) {
        super(props);
        this.state = {};
    }

    public static getDerivedStateFromError(error: Error) {
        return { error };
    }

    public override render() {
        const { locale, children } = this.props;
        const { error } = this.state;
        const shouldRenderFallback =
            isContentNotFoundError(error) ||
            isContentFetchError(error) ||
            isRelayError(error);

        if (shouldRenderFallback) {
            return (
                <TemplateInitialSearchResults
                    searchKey="recommended"
                    searchQuery={parseSearchQuery(null, {
                        withLocales: [locale]
                    })}
                    sortLookup={CategoryTemplateOrder["recommended"]}
                />
            );
        } else if (error != null) {
            throw error;
        } else {
            return children;
        }
    }
}

type TemplateRecommendationsProps = {
    locale: string;
};

export const TemplateRecommendations = ({
    locale
}: TemplateRecommendationsProps) => {
    const recommendationData =
        useLazyLoadQuery<templateRecommendationsSearchResultsRelayQuery>(
            TemplateRecommendationsSearchResultsRelayQuery,
            getTemplateRecommendationsSearchResultsQueryVars(location)
        );

    const searchQuery = useMemo(() => {
        return parseSearchQuery(
            expectContent(
                recommendationData.growthRecommendations?.recommendations?.data,
                "Expected recommendations to be defined"
            ).map((rec) => rec.entityId),
            { withLocales: [locale] }
        );
    }, [recommendationData, locale]);

    return (
        <TemplateInitialSearchResults
            searchKey="recommended"
            searchQuery={searchQuery}
            sortLookup={CategoryTemplateOrder["recommended"]}
        />
    );
};

export const TemplateRecommendationsSearchResults = () => {
    const locale = useQueryLocale();
    return (
        <TemplateRecommendationsErrorBoundary locale={locale}>
            <TemplateRecommendations locale={locale} />
        </TemplateRecommendationsErrorBoundary>
    );
};
